// [S-637 slice 2] Global re-auth modal.
//
// When a session lapses mid-task the AuthProvider flips to `expired` (retaining
// the last session), and App keeps the authenticated route tree MOUNTED — so a
// half-written draft or an open review is still there underneath. This overlay
// blocks interaction and lets the user sign back in *in place*: on success the
// state returns to `authenticated`, the modal closes, and they pick up exactly
// where they were. No hard redirect to /login, no lost work.
//
// It reuses `reauthenticate` (not `signIn`) so re-auth never dips through the
// global `authenticating` state — that would drop App back to the Login routes
// and unmount the very work we're preserving.
import { type FormEvent, useState } from "react";
import { useAuth } from "./AuthProvider";
import { Button, Dialog } from "../components";

export function SessionExpiredModal(): JSX.Element | null {
  const { state, reauthenticate, signOut } = useAuth();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const expired = state.status === "expired";
  // Read the email off the retained session so the user needn't retype it.
  const email = state.status === "expired" ? state.session.email : "";

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setNote(null);
    try {
      await reauthenticate({ email, password, provider: "password" });
      // Success flips state → authenticated → this component returns null.
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed. Try again.");
    } finally {
      setSubmitting(false);
      setPassword("");
    }
  }

  async function handleMagicLink(): Promise<void> {
    setSubmitting(true);
    setError(null);
    setNote(null);
    try {
      await reauthenticate({ email, provider: "magic" });
    } catch (err) {
      // The Firebase adapter signals a sent link by throwing MagicLinkSentError
      // — that's success copy, not a failure. Anything else is a real error.
      if (err instanceof Error && err.name === "MagicLinkSentError") {
        setNote(err.message);
      } else {
        setError(err instanceof Error ? err.message : "Couldn't send a magic link.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!expired) return null;

  return (
    <Dialog
      open
      // Blocking — the session is gone; the only ways forward are re-auth or
      // sign-out. Ignore Esc / overlay-click close requests.
      onOpenChange={() => {}}
      contentClassName="ds-dialog__content reauth"
      title={<span data-testid="session-expired-modal">Your session expired</span>}
      description={
        <>
          Signed in as <b>{email}</b>. Sign back in to pick up right where you
          left off — your work on this page is still here.
        </>
      }
    >
      <form className="reauth__form" onSubmit={(e) => void handleSubmit(e)}>
        <div className="reauth__field">
          <label htmlFor="reauth-password">Password</label>
          <input
            id="reauth-password"
            type="password"
            autoFocus
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            data-testid="reauth-password"
          />
        </div>

        <Button
          type="submit"
          variant="bare"
          className="reauth__primary"
          disabled={submitting || password.length === 0}
          data-testid="reauth-submit"
        >
          {submitting ? "Signing in…" : "Sign back in →"}
        </Button>

        <Button
          type="button"
          variant="bare"
          className="reauth__secondary"
          onClick={() => void handleMagicLink()}
          disabled={submitting}
          data-testid="reauth-magic"
        >
          Email me a magic link instead
        </Button>

        {note !== null && (
          <p className="reauth__note" role="status" data-testid="reauth-note">
            {note}
          </p>
        )}
        {error !== null && (
          <p className="reauth__error" role="alert" data-testid="reauth-error">
            {error}
          </p>
        )}

        <button
          type="button"
          className="reauth__signout"
          onClick={() => void signOut()}
          disabled={submitting}
          data-testid="reauth-signout"
        >
          Sign out instead
        </button>
      </form>
    </Dialog>
  );
}
